import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect } from "react";

import { addToast } from "@/components/base/toast";
import {
  useCanUpdateTournament,
  useTournament,
} from "@/components/tournaments/context";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";

/**
 * Fetches TD notifications for the current tournament and shows
 * warning toasts. Only fires when the viewer is a TD/admin.
 * Re-fires on tab or division navigation within the tournament.
 */
export function useTdNotifications() {
  const canUpdate = useCanUpdateTournament();
  const tournament = useTournament();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { divisionId, tab } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const { data: notifications } = useQuery({
    ...trpc.tournaments.tdNotifications.queryOptions({
      tournamentId: tournament.id,
    }),
    enabled: canUpdate,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!notifications) return;

    for (const n of notifications) {
      if (
        n.type === "unverified_division" &&
        !(n.divisionId.toString() === divisionId && tab === "playoffs")
      ) {
        const division = tournament.tournamentDivisions.find(
          (d) => d.id === n.divisionId,
        );
        const name = division
          ? getTournamentDivisionDisplay(division)
          : "Division";

        addToast({
          key: `${n.type}:${n.divisionId}`,
          variant: "warning",
          title: `${name} not verified`,
          description:
            "All playoff matches are complete. Please verify the division results.",
          action: {
            label: "View Playoffs",
            onPress: () =>
              navigate({
                to: "/tournaments/$tournamentId/$divisionId/{-$tab}",
                params: {
                  tournamentId: tournament.id.toString(),
                  divisionId: n.divisionId.toString(),
                  tab: "playoffs",
                },
              }),
          },
        });
      }
    }
  }, [
    notifications,
    tournament.tournamentDivisions,
    navigate,
    tournament.id,
    divisionId,
    tab,
  ]);
}
